import { theme } from '@dojo/framework/core/middleware/theme';
import { create, v, w } from '@dojo/framework/core/vdom';
import { contentTrack, createNlsMiddleware, keymap, pathmap, userstate } from '@holmescorp/lcc-state';
import { Icon } from '@holmescorp/base-widgets';
import { MicroCredentialCard } from '@holmescorp/card';
import { ScoreChart } from '@holmescorp/progress-chart';
import { publish } from '@holmescorp/pub-sub';
import css from '../css/study.m.css';
import progressCss from '../css/progress-view.m.css';
import { MicroCardButton, ContentContainer, MicroProgress } from './index';
const factory = create({
    theme,
    contentTrack,
    userstate,
    ditabookNls: createNlsMiddleware('ditabook')
}).properties();
export const MicroCard = factory(({ id, properties, children, middleware: { theme, contentTrack, userstate, ditabookNls } }) => {
    const { topic, onAction, showProgress = false, bestQuizScore: bestScore = 0, titleOnly = false } = properties();
    const fixedCss = theme.classes(css);
    const themeCss = theme.classes(progressCss);
    const { messages: { card_button_label_start = '[ditabook] card_button_label_start', card_button_label_start_quiz = '[ditabook] card_button_label_start_quiz', card_button_label_middle = '[ditabook] card_button_label_middle', card_button_label_finished = '[ditabook] card_button_label_finished', use_progress_chart_color = 'false', assess_progress_threshold_high = '80' } } = ditabookNls();
    const status = userstate.get(userstate.path('status'));
    const key = topic.attributes?.key || '';
    const { path } = keymap.get(key);
    const { view } = pathmap.get(path);
    const root = status[key].parent;
    const { tracked } = contentTrack;
    const tracking = tracked.content[root].filter((t) => t.topicset === key)[0];
    const { title, percent, isreadingstarted, isreadingcompleted, isquizcompleted } = tracking;
    const useProgressChartColor = use_progress_chart_color === 'true';
    const passingScore = parseInt(assess_progress_threshold_high) || 80;
    const topicgroups = topic.children
        ?.filter((child) => 'topicgroup' in child)
        .map((tg) => ({
        attributes: tg.topicgroup.attributes,
        progress: status[tg.topicgroup.attributes?.key || ''].data
    })) || [];
    const readTime = topicgroups.reduce((acc, child) => {
        const time = child.attributes?.readtime || 0;
        return acc + time;
    }, 0);
    const totalComplete = topicgroups.reduce((count, child) => {
        const topicdata = status[child.attributes?.key || ''];
        if (topicdata.data.complete == topicdata.data.total) {
            count += 1;
        }
        return count;
    }, 0);
    const rawScore = percent === 1 ? 100 : percent * 100;
    const isquizpassed = rawScore >= passingScore;
    const score = Math.round(isquizcompleted ? rawScore : -1);
    return v('div', {
        id,
        classes: [theme.variant(), fixedCss.cardContainer]
    }, [
        w(ContentContainer, { tabIndex: 0, additionalClasses: [fixedCss.card] }, [
            ...children(),
            w(MicroCredentialCard, { xqa: `micro-credential-card-${key}` }, [
                {
                    leading: v('div', { classes: [fixedCss.leading] }, [
                        v('img', {
                            title,
                            src: `${window.hcstatic.portal_cdn}/js/images/${window.hcstatic.portal_site}/icons/icon_${key}.svg`
                        })
                    ]),
                    trailing: !titleOnly
                        ? v('div', { classes: [fixedCss.trailing] }, [
                            v('div', {
                                classes: [fixedCss.alphaHalfBlack, fixedCss.basefont],
                                xqa: 'estimated-read-time'
                            }, [`${readTime} minutes`]),
                            v('div', { classes: [fixedCss.titlefont], xqa: 'progress-area-title' }, [
                                title
                            ]),
                            w(MicroProgress, {
                                label: 'Reading Progress',
                                value: totalComplete,
                                min: 0,
                                max: topicgroups.length
                            })
                        ])
                        : v('div', { classes: [fixedCss.trailing] }, [
                            v('div', { classes: [fixedCss.titlefont], xqa: 'progress-area-title' }, [title])
                        ]),
                    actions: v('div', { classes: [themeCss.actionContainer] }, [
                        showProgress && bestScore
                            ? v('div', { classes: [themeCss.innerContainer] }, [
                                w(ScoreChart, {
                                    score,
                                    bestScore,
                                    insideLabelText: `Quiz Score`,
                                    useScoreColor: useProgressChartColor,
                                    useBestScoreIcon: false
                                })
                            ])
                            : null,
                        showProgress && isreadingstarted
                            ? v('div', { classes: [themeCss.innerContainer] }, [
                                v('p', {
                                    classes: [themeCss.readingProgress],
                                    xqa: `reading-progress-${key}`
                                }, [`Total Reading Progress: ${totalComplete} of ${topicgroups.length}`]),
                                v('div', { xqa: `reading-progress-bars-${key}` }, topicgroups.map((tg) => {
                                    return w(MicroProgress, {
                                        label: tg.attributes?.title,
                                        min: 0,
                                        max: tg.progress.total,
                                        value: tg.progress.complete,
                                        showOutput: false,
                                        additionalClasses: [themeCss.progressContainer]
                                    });
                                }))
                            ])
                            : null,
                        v('div', { classes: [themeCss.actionButtons, !showProgress ? themeCss.wideButtons : null] }, [
                            v('div', {
                                key: `${key}-topicgroup`,
                                classes: [themeCss.actionButton]
                            }, [
                                w(MicroCardButton, {
                                    labelText: isreadingstarted
                                        ? isreadingcompleted
                                            ? card_button_label_finished
                                            : card_button_label_middle
                                        : card_button_label_start,
                                    showIcon: true,
                                    darkMode: !isreadingcompleted,
                                    icon: isreadingcompleted
                                        ? w(Icon, { faname: 'rotate-left' })
                                        : null,
                                    xqa: `topicgroup-reading-action-${key}`,
                                    onClick: () => {
                                        onAction && onAction();
                                        view();
                                    },
                                    onDown: () => {
                                        onAction && onAction();
                                        view();
                                    }
                                })
                            ]),
                            v('div', {
                                xqa: `quiz-action-button-${key}`,
                                classes: [themeCss.actionButton]
                            }, [
                                w(MicroCardButton, {
                                    labelText: card_button_label_start_quiz,
                                    showIcon: false,
                                    darkMode: isreadingcompleted && !isquizpassed,
                                    onClick: () => {
                                        const { path } = keymap.get(key);
                                        publish(`site/quiz`, path, key);
                                    },
                                    onDown: () => {
                                        const { path } = keymap.get(key);
                                        publish(`site/quiz`, path, key);
                                    }
                                })
                            ])
                        ])
                    ])
                }
            ])
        ])
    ]);
});
export default MicroCard;
